<template>
  <div>
    <v-container fluid>
      <v-row dense class="pb-0">
        <v-col cols="3">
          <date-range :from.sync="from" :to.sync="to" v-model="dateDisplay" @change="refresh()" hide-details option="Last 30 days"/>
        </v-col>
        <v-col cols="3" v-if="sites.length>1">
          <v-autocomplete label="Site" dense outlined hide-details :items="[{id:null,name:'All'},{divider:true},...sites]" v-model="siteId" item-text="name" item-value="id" @change="refresh()"/>
        </v-col>
        <v-col cols="3">
          <v-autocomplete label="Product" dense outlined hide-details :items="[{id:null,name:'All'},{divider:true},...products]" v-model="productId" item-text="name" item-value="id" @change="totals()"/>
        </v-col>
        <v-col class="text-right pr-2">
          <v-btn icon @click="printHtml('Bottling activity', report())"><v-icon>mdi-printer</v-icon></v-btn>
          <v-tooltip bottom open-delay="1000" v-if="company.allowCSVExport">
            <template v-slot:activator="{on}">
              <v-btn icon @click="exportCSV" v-on="on"><v-icon>mdi-download</v-icon></v-btn>
            </template>
            Export as CSV file
          </v-tooltip>
        </v-col>
      </v-row>
    </v-container>

    <v-card class="mx-4 pa-4 overflow-y" height="calc(100vh - 184px)">
      <span v-html="report()"/>
    </v-card>

  </div>
</template>

<script>
import fileSaver from 'file-saver';
import printHtml from '@/mixins/printHtml'

export default {
  name: 'ReportsBottlingActivity',
  mixins: [printHtml],
  data() {
    return {
      from: null,
      to: null,
      dateDisplay: '',
      siteId: null,
      products: [],
      productId: null,
      loading: true,
      transactions: [],
      totalBottles: 0,
      totalCases: 0
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    list() {
      return this.productId ? this.transactions.filter(t => t.productId === this.productId) : this.transactions;
    },
    sites() {
      return this.$store.getters.sites(true, {B263:true,B265:true,B266:true});
    }
  },
  async created() {
    this.$emit('name', 'Bottling activity')
    if (this.$route.query.from && this.$route.query.to) {
      this.from = this.$route.query.from;
      this.to = this.$route.query.to;
    }
    this.$lazyListLoad(this.products, 'products2', 'id,name', 100, [{field:'active', comparator:'eq', value:'true'}]);
    this.productId = this.$route.query.productId ?? null;
  },
  methods: {
    async refresh() {
      this.loading = true;
      const results = await this.$apiCall(`query ReportsBottlingActivity($from:Date!,$to:Date!,$siteId:ID) {
                                                productsActivity(from:$from,to:$to,siteId:$siteId,bottlingOnly:true) {
                                                  date,bottles,cases,lot,productName,productId
                                                }
                                              }`,
                                          {from:this.from,to:this.to,siteId:this.siteId});
      if (!results.error) {
        this.transactions = results.data.productsActivity;
        this.totals();
      }
      this.loading = false;

    },
    totals() {
      this.totalBottles = 0;
      this.totalCases = 0;
      this.list.forEach(transaction => {this.totalBottles+=transaction.bottles;this.totalCases+=transaction.cases});
    },

    report() {
      const lines = [
                    `<style>
                      @page {size:landscape}
                      .reports-bottling-activity {font-family: Roboto, sans-serif; width:100%;border-spacing:0;font-size:12px}
                      .reports-bottling-activity th {height:36px;padding:0 16px;vertical-align:middle}
                      .reports-bottling-activity .spacer {height:18px}
                      .reports-bottling-activity td {height:36px;padding:0 16px;vertical-align:middle}
                      .reports-bottling-activity .title {font-size:18px;text-transform:uppercase}
                      .reports-bottling-activity .sup-title {font-weight:normal;font-size:22px}
                      .reports-bottling-activity .sub-title {font-weight:normal;height:inherit;padding-bottom:20px;font-size:14px}
                      .reports-bottling-activity .header th {border-bottom:1px solid #000;margin-bottom:20px}
                      .reports-bottling-activity .text-left {text-align:left}
                      .reports-bottling-activity .text-right {text-align:right}
                      .reports-bottling-activity .font-weight-bold {font-weight:bold}
                      .reports-bottling-activity .total-row {font-weight:bold}
                    </style>
                    <table class="reports-bottling-activity">
                      <thead>
                          <tr>
                            <th colspan="6" class="sup-title">${this.company.name}</th>
                          </tr>
                          <tr>
                            <th colspan="6" class="title">Bottling activity</th>
                          </tr>
                          <tr>
                            <th colspan="6" class="sub-title">${this.dateDisplay}</th>
                          </tr>

                          <tr class="header">
                              <th width="15%" class="text-left">Date</th>
                              <th width="40%" class="text-left">Product</th>
                              <th width="15%" class="text-left">Lot</th>
                              <th width="15%" class="text-right">Bottles</th>
                              <th width="15%" class="text-right">Cases</th>
                            </tr>
                        </thead>
                        <tbody>`
      ];
      for (const transaction of this.list) {
        lines.push(`<tr>`);
        lines.push(`<td>${this.$formatDate(transaction.date)}</td>`);
        lines.push(`<td>${transaction.productName}</td>`);
        lines.push(`<td>${transaction.lot}</td>`);
        lines.push(`<td class="text-right">${this.$number(transaction.bottles,0)}</td>`);
        lines.push(`<td class="text-right">${this.$number(transaction.cases,1)}</td>`);
        lines.push(`</tr>`);
      }
      lines.push(`<tr :class="dark ? 'total-row-dark' : 'total-row'"><td/><td/><td class="text-right">Total</td><td class="text-right">${this.$number(this.totalBottles,0)}</td><td class="text-right">${this.$number(this.totalCases,0)}</td>`);
      lines.push(`</tbody>`);
      lines.push(`</table>`);
      return lines.join('');
    },

    exportCSV() {
      const lines = [ `"${this.company.name}",,,,`,
                      `"Bottling activity",,,,`,
                      `"${this.dateDisplay}",,,,`,
                      `"Date","Product","Lot","Bottles","Cases"`
                    ];
      for (const transaction of this.list)
        lines.push(`"${transaction.date}","${transaction.productName}","${transaction.lot}","${this.$number(transaction.bottles,0)}","${this.$number(transaction.cases,0)}"`);
      lines.push(`,,"Total","${this.$number(this.totalBottles,0)}","${this.$number(this.totalCases,0)}"`);

      // Write CSV file
      const blob = new Blob([lines.join('\r\n')], {type: "text/plain;charset=utf-8"});
      fileSaver.saveAs(blob, 'Bottling activity.csv');
    }
  }
}
</script>